// ============================================================================
// fx.js — pure-visual particle systems, all pooled and drawn in a handful of
// calls: ember hit-sparks + dust + block flashes (one Points cloud), globally
// drifting ash (one Points cloud), expanding shockwave rings (one InstancedMesh)
// and archer ember-bolts (one InstancedMesh). Spark/bolt state lives in world
// pools; this module integrates the visual motion at render rate and streams the
// GPU buffers. No shadows, additive blending, cheap.
// ============================================================================

import * as THREE from 'three';
import { MAX_HITSPARKS, MAX_PROJECTILES, GRAVITY } from '../core/constants.js';

// spark kind → base colour
const SPARK_COL = [
  [1.0, 0.42, 0.12],  // 0 ember
  [0.55, 0.5, 0.45],  // 1 dust
  [0.7, 0.82, 1.0],   // 2 block flash
  [0.15, 0.12, 0.14], // 3 black (titanfall)
];

const SPARK_VERT = `
  attribute vec3 aColor; attribute float aSize; attribute float aAlpha;
  varying vec3 vColor; varying float vAlpha;
  void main(){
    vColor = aColor; vAlpha = aAlpha;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = aSize * (300.0 / -mv.z);
    gl_Position = projectionMatrix * mv;
  }`;
const SPARK_FRAG = `
  varying vec3 vColor; varying float vAlpha;
  void main(){
    vec2 d = gl_PointCoord - 0.5;
    float r = dot(d,d);
    if(r > 0.25) discard;
    float a = vAlpha * (1.0 - r*4.0);
    gl_FragColor = vec4(vColor, a);
  }`;

export class FX {
  constructor(scene, camera) {
    this.camera = camera;
    this.ashFall = 1;          // biome-tunable fall-speed multiplier for the drift cloud
    this._buildSparks(scene);
    this._buildAsh(scene);
    this._buildShocks(scene);
    this._buildBolts(scene);
  }

  // Re-skin the drifting ambient cloud for a biome (ash / snow / leaves / pollen / none).
  setAmbient(mode) {
    const m = this.ash.material;
    switch (mode) {
      case 'snow':   m.color.setHex(0xdfe7f0); m.size = 0.42; m.opacity = 0.8;  this.ash.visible = true; this.ashFall = 0.45; break;
      case 'leaves': m.color.setHex(0xb5793a); m.size = 0.5;  m.opacity = 0.6;  this.ash.visible = true; this.ashFall = 0.7;  break;
      case 'pollen': m.color.setHex(0xcabd6e); m.size = 0.3;  m.opacity = 0.45; this.ash.visible = true; this.ashFall = 0.35; break;
      case 'none':   this.ash.visible = false; this.ashFall = 1; break;
      default:       m.color.setHex(0x6b6058); m.size = 0.35; m.opacity = 0.5;  this.ash.visible = true; this.ashFall = 1; break; // ash
    }
  }

  _buildSparks(scene) {
    const g = new THREE.BufferGeometry();
    this.sPos = new Float32Array(MAX_HITSPARKS * 3);
    this.sCol = new Float32Array(MAX_HITSPARKS * 3);
    this.sSize = new Float32Array(MAX_HITSPARKS);
    this.sAlpha = new Float32Array(MAX_HITSPARKS);
    g.setAttribute('position', new THREE.BufferAttribute(this.sPos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aColor', new THREE.BufferAttribute(this.sCol, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aSize', new THREE.BufferAttribute(this.sSize, 1).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aAlpha', new THREE.BufferAttribute(this.sAlpha, 1).setUsage(THREE.DynamicDrawUsage));
    g.setDrawRange(0, 0);
    const m = new THREE.ShaderMaterial({
      vertexShader: SPARK_VERT, fragmentShader: SPARK_FRAG,
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.sparks = new THREE.Points(g, m);
    this.sparks.frustumCulled = false;
    scene.add(this.sparks);
  }

  _buildAsh(scene) {
    const N = 900;
    this.ashN = N;
    const g = new THREE.BufferGeometry();
    this.ashPos = new Float32Array(N * 3);
    this.ashVel = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      this.ashPos[i * 3] = (Math.random() - 0.5) * 160;
      this.ashPos[i * 3 + 1] = Math.random() * 60;
      this.ashPos[i * 3 + 2] = (Math.random() - 0.5) * 160;
      this.ashVel[i * 3] = (Math.random() - 0.5) * 0.6;
      this.ashVel[i * 3 + 1] = -0.4 - Math.random() * 0.5;
      this.ashVel[i * 3 + 2] = (Math.random() - 0.5) * 0.6;
    }
    g.setAttribute('position', new THREE.BufferAttribute(this.ashPos, 3).setUsage(THREE.DynamicDrawUsage));
    const m = new THREE.PointsMaterial({
      color: 0x6b6058, size: 0.35, transparent: true, opacity: 0.5,
      depthWrite: false, sizeAttenuation: true,
    });
    this.ash = new THREE.Points(g, m);
    this.ash.frustumCulled = false;
    scene.add(this.ash);
  }

  _buildShocks(scene) {
    const ring = new THREE.RingGeometry(0.82, 1.0, 40).rotateX(-Math.PI / 2);
    const m = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0.9,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    });
    this.shocks = new THREE.InstancedMesh(ring, m, 32);
    this.shocks.frustumCulled = false;
    this.shocks.count = 0;
    this.shocks.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(32 * 3), 3);
    scene.add(this.shocks);
  }

  _buildBolts(scene) {
    const geo = new THREE.BoxGeometry(0.12, 0.12, 0.9);
    const m = new THREE.MeshBasicMaterial({ color: 0xff7a2a, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false });
    this.bolts = new THREE.InstancedMesh(geo, m, MAX_PROJECTILES);
    this.bolts.frustumCulled = false;
    this.bolts.count = 0;
    scene.add(this.bolts);
  }

  // ---- update at render rate ----
  update(world, dt) {
    this._updateSparks(world, dt);
    this._updateAsh(dt);
    this._updateShocks(world);
    this._updateBolts(world);
  }

  _updateSparks(world, dt) {
    // spawn dodge dust puffs if requested
    if (world._dust) {
      world._dust = false;
      const p = world.player;
      for (let i = 0; i < 6; i++) {
        const a = Math.random() * 6.28, sp = 1 + Math.random() * 2.5;
        world.spawnSpark(p.x, 0.15, p.z, Math.cos(a) * sp, 0.6 + Math.random(), Math.sin(a) * sp, 0.4, 1.2, 1);
      }
    }
    const arr = world.sparks.active;
    for (let i = arr.length - 1; i >= 0; i--) {
      const s = arr[i];
      s.life -= dt;
      if (s.life <= 0) { world.sparks.despawn(s); continue; }
      s.vy -= (s.kind === 1 ? GRAVITY * 0.25 : GRAVITY * 0.5) * dt;
      s.x += s.vx * dt; s.y += s.vy * dt; s.z += s.vz * dt;
      s.vx *= 0.92; s.vz *= 0.92;
      if (s.y < 0) { s.y = 0; s.vy *= -0.3; }
    }
    // stream to GPU
    const n = arr.length;
    for (let i = 0; i < n; i++) {
      const s = arr[i];
      const c = SPARK_COL[s.kind] || SPARK_COL[0];
      const t = s.life / s.maxLife;
      this.sPos[i * 3] = s.x; this.sPos[i * 3 + 1] = s.y; this.sPos[i * 3 + 2] = s.z;
      const bright = s.kind === 3 ? 1.0 : (0.6 + t * 0.8);
      this.sCol[i * 3] = c[0] * bright; this.sCol[i * 3 + 1] = c[1] * bright; this.sCol[i * 3 + 2] = c[2] * bright;
      this.sSize[i] = s.size * (0.4 + t) * 6;
      this.sAlpha[i] = Math.min(1, t * 1.6);
    }
    const g = this.sparks.geometry;
    g.setDrawRange(0, n);
    g.attributes.position.needsUpdate = true;
    g.attributes.aColor.needsUpdate = true;
    g.attributes.aSize.needsUpdate = true;
    g.attributes.aAlpha.needsUpdate = true;
  }

  _updateAsh(dt) {
    if (!this.ash.visible) return;
    const cam = this.camera.position;
    const P = this.ashPos, V = this.ashVel, N = this.ashN, f = this.ashFall;
    for (let i = 0; i < N; i++) {
      const o = i * 3;
      P[o] += V[o] * dt * 6 * f; P[o + 1] += V[o + 1] * dt * 6 * f; P[o + 2] += V[o + 2] * dt * 6 * f;
      // wrap around the camera so ash is always present
      if (P[o + 1] < 0) { P[o + 1] = 55; }
      if (P[o] - cam.x < -80) P[o] += 160; else if (P[o] - cam.x > 80) P[o] -= 160;
      if (P[o + 2] - cam.z < -80) P[o + 2] += 160; else if (P[o + 2] - cam.z > 80) P[o + 2] -= 160;
    }
    this.ash.geometry.attributes.position.needsUpdate = true;
  }

  _updateShocks(world) {
    const arr = world.shockwaves;
    let n = 0;
    const col = this.shocks.instanceColor.array;
    for (let i = 0; i < arr.length && n < 32; i++) {
      const w = arr[i];
      if (!w.active) continue;
      _m.makeScale(w.r, 1, w.r);
      _m.setPosition(w.x, 0.15, w.z);
      this.shocks.setMatrixAt(n, _m);
      const fade = 1 - w.life / w.maxLife;
      if (w.kind === 1) { col[n * 3] = 1.0 * fade; col[n * 3 + 1] = 0.45 * fade; col[n * 3 + 2] = 0.12 * fade; }
      else { col[n * 3] = 0.35 * fade; col[n * 3 + 1] = 0.12 * fade; col[n * 3 + 2] = 0.2 * fade; }
      n++;
    }
    this.shocks.count = n;
    this.shocks.instanceMatrix.needsUpdate = true;
    this.shocks.instanceColor.needsUpdate = true;
  }

  _updateBolts(world) {
    const arr = world.projectiles.active;
    let n = 0;
    for (let i = 0; i < arr.length; i++) {
      const b = arr[i];
      const yaw = Math.atan2(b.vx, b.vz);
      _e.set(0, yaw, 0);
      _q.setFromEuler(_e);
      _p.set(b.x, b.y, b.z);
      _s.set(1, 1, 1);
      _m.compose(_p, _q, _s);
      this.bolts.setMatrixAt(n, _m);
      n++;
    }
    this.bolts.count = n;
    this.bolts.instanceMatrix.needsUpdate = true;
  }
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
